import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { cognitoAdminService } from '../../services/cognitoAdminService';
import { databaseService } from '../../services/databaseService';
import { cognitoConfig } from '../Constants';
import styles from './Dashboard.module.css';

// Reports.js'deki DISTRICT_FILTERS - bölge listesi için
const DISTRICT_FILTERS = {
  "Akyurt": ["akyurt"],
  "Beypazarı": ["beypazari", "beypazarı", "beypazari"],
  "Çankaya": ["ankaya", "çankaya", "?ankaya", "caldag", "caldağ", "tbmm", "?alda?", "?aldag", "?ankaya/?alda?", "?ankaya/tbmm"],
  "Elmadağ": ["elmadağ", "elmadag", "elmada? barutsan fabrikasi", "elmada? radar sahasi", "elmada?/ye?ildere"],
  "Etimesgut": ["etimesgut", "etimesgut havalimanı", "etimesgut havalimani", "et?mesgut haval?mani", "etimesgut"],
  "Gölbaşı": ["golbasi", "gölbaşı", "g?lba?i", "genclik parki", "gençlik parkı", "ufuk danisman", "ufuk danışman", "altındağ", "g?lba?i/ah?boz k?y?", "g?lba?i/ufuk dani?ment", "g?lba?i/ya?lipinar k?y?"],
  "Haymana": ["haymana", "haymana tarim"],
  "Keçiören": ["kecoren", "keçiören", "mgm", "ke???ren/9. (ankara) b?lge", "ke???ren/ba?lum", "ke?i?ren/mgm"],
  "Kazan": ["kazan", "kahramankazan", "kazan/kurtbo?azi baraji", "kazan/sarayk?y toprak"],
  "Mamak": ["mamak", "bayindir baraji", "bayındır barajı", "mamak/bayindir baraji"],
  "Nallıhan": ["nallihan", "nallıhan", "nallihan/sariyar"],
  "Polatlı": ["polatli", "polatlı"],
  "Pursaklar": ["pursaklar"],
  "Sincan": ["sincan", "temelli", "s?ncan", "s?ncan/temell?"],
  "Yenimahalle": ["yenimahalle"]
};

const UserManagement = ({ onUserListChange }) => {
  const { user, login } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUserForRegions, setSelectedUserForRegions] = useState(null);
  const [userRegions, setUserRegions] = useState([]); // Seçili kullanıcının bölgeleri
  const [availableRegions] = useState(Object.keys(DISTRICT_FILTERS).sort()); // Tüm bölgeler
  const [viewingRegionsFor, setViewingRegionsFor] = useState(null); // Hangi kullanıcının bölgeleri görüntüleniyor
  const [expandedParents, setExpandedParents] = useState({}); // Hangi parent hesaplar açık
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    role: 'Municipality-Technical',
  });

  // AWS Cognito Admin API ile kullanıcı listesi çek
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersList = await cognitoAdminService.listUsers();
      
      // Kullanıcıları formatla
      const formattedUsers = usersList.map(user => {
        // Attributes array'inden email ve role bilgisini al
        const emailAttr = user.attributes?.find(attr => attr.Name === 'email') || 
                         (typeof user.email === 'string' ? { Value: user.email } : null);
        const email = emailAttr?.Value || user.email || 'N/A';
        
        const roleAttr = user.attributes?.find(attr => attr.Name === 'custom:role');
        const role = roleAttr?.Value || user.role || user.groups?.[0] || 'Tanımsız';
        
        // Bölge bilgisini al (custom:regions attribute'undan)
        const regionsAttr = user.attributes?.find(attr => attr.Name === 'custom:regions');
        let regions = [];
        if (regionsAttr?.Value) {
          try {
            regions = JSON.parse(regionsAttr.Value);
          } catch (e) {
            // Eğer JSON değilse, virgülle ayrılmış string olabilir
            regions = regionsAttr.Value.split(',').map(r => r.trim()).filter(Boolean);
          }
        }
        
        return {
          username: user.username,
          email: email,
          role: role,
          regions: regions,
          status: user.status === 'CONFIRMED' ? 'Aktif' : 
                  user.status === 'FORCE_CHANGE_PASSWORD' ? 'Şifre Değiştirilmeli' : 
                  user.status || 'Bilinmiyor',
          enabled: user.enabled !== false,
          userCreateDate: user.userCreateDate,
        };
      });
      
      setUsers(formattedUsers);
      
      // Parent component'e kullanıcı sayısını bildir
      if (onUserListChange) {
        onUserListChange(formattedUsers.length);
      }
    } catch (err) {
      console.error('Kullanıcı listesi yükleme hatası:', err);
      // Hata mesajını sadece console'da göster, kullanıcıya gösterme
      // setError('Kullanıcı listesi yüklenemedi: ' + err.message);
      
      // Hata durumunda parent'a null gönder
      if (onUserListChange) {
        onUserListChange(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // AWS Cognito Admin API ile kullanıcı oluştur
      const newUser = await cognitoAdminService.createUser({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role, // Role'ü de gönder (createUser içinde group'a eklemek için)
      });

      // Kullanıcıyı group'a ekle (role için) - createUser içinde de ekleniyor ama yine de kontrol edelim
      if (formData.role && newUser) {
        try {
          await cognitoAdminService.addUserToGroup(formData.username, formData.role);
          console.log(`✅ Kullanıcı ${formData.username} ${formData.role} grubuna eklendi`);
        } catch (groupError) {
          console.warn('⚠️ Kullanıcı oluşturuldu ancak gruba eklenemedi:', groupError);
          // Kullanıcı oluşturuldu ama gruba eklenemedi - devam et
        }
      }
      
      // Listeyi yenile
      await loadUsers();
      
      setFormData({ email: '', username: '', password: '', role: 'Municipality-Technical' });
      setShowAddUser(false);
      
      // Başarı mesajı
      console.log('✅ Kullanıcı başarıyla oluşturuldu ve gruba eklendi!');
    } catch (err) {
      console.error('❌ Kullanıcı oluşturma hatası:', err);
      setError('Kullanıcı oluşturulamadı: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`${username} kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) {
      return;
    }

    try {
      setError(null);
      await cognitoAdminService.deleteUser(username);
      
      // Listeyi yenile
      await loadUsers();
      console.log('✅ Kullanıcı başarıyla silindi');
    } catch (err) {
      console.error('❌ Kullanıcı silme hatası:', err);
      setError('Kullanıcı silinemedi: ' + err.message);
    }
  };

  const handleOpenRegionModal = async (userItem) => {
    setSelectedUserForRegions(userItem);
    setUserRegions(userItem.regions || []);
  };

  const handleCloseRegionModal = () => {
    setSelectedUserForRegions(null);
    setUserRegions([]);
  };

  const handleRegionToggle = (region) => {
    setUserRegions(prev => 
      prev.includes(region)
        ? prev.filter(r => r !== region)
        : [...prev, region]
    );
  };

  const handleSaveRegions = async () => {
    if (!selectedUserForRegions) return;

    try {
      setError(null);
      setIsSubmitting(true);

      // Bölgeleri JSON string olarak kaydet
      await cognitoAdminService.updateUserAttributes(selectedUserForRegions.username, {
        regions: JSON.stringify(userRegions)
      });

      // Listeyi yenile
      await loadUsers();
      
      // Modal'ı kapat
      handleCloseRegionModal();
      
      console.log('✅ Kullanıcı bölgeleri başarıyla güncellendi');
    } catch (err) {
      console.error('❌ Bölge güncelleme hatası:', err);
      setError('Bölgeler güncellenemedi: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const roles = [
    'Admin',
    'Municipality-Admin',
    'Municipality-Technical',
    'Municipality-Landscape',
    'Operator',
    'Example',
    'Industry',
  ];

  // Hiyerarşik yapı: Hangi roller hangi parent'a bağlı
  const roleHierarchy = {
    'Municipality-Admin': ['Municipality-Technical', 'Operator', 'Municipality-Landscape'],
    'Admin': ['Example']
  };

  // Kullanıcıları hiyerarşik yapıya göre grupla
  const organizeUsersHierarchically = (usersList) => {
    const parentUsers = [];
    const childUsersByParent = {}; // Her parent kullanıcının alt hesapları
    const standaloneUsers = [];

    // Önce parent ve child kullanıcıları ayır
    const municipalityAdmins = [];
    const admins = [];
    const municipalityChildren = []; // Municipality-Admin'in alt hesapları
    const exampleUsers = []; // Admin'in alt hesapları

    usersList.forEach(userItem => {
      const role = userItem.role;
      const roleLower = role?.toLowerCase();
      
      if (role === 'Municipality-Admin' || roleLower === 'municipality-admin') {
        municipalityAdmins.push(userItem);
      } else if (role === 'Admin' || roleLower === 'admin') {
        admins.push(userItem);
      } else if (roleHierarchy['Municipality-Admin']?.includes(role) || 
                 roleHierarchy['Municipality-Admin']?.some(r => r.toLowerCase() === roleLower)) {
        municipalityChildren.push(userItem);
      } else if (role === 'Example' || roleLower === 'example') {
        exampleUsers.push(userItem);
      } else {
        standaloneUsers.push(userItem);
      }
    });

    // Municipality-Admin kullanıcılarını ekle
    municipalityAdmins.forEach(parentUser => {
      parentUsers.push({ ...parentUser, type: 'parent', parentRole: 'Municipality-Admin' });
      // Her Municipality-Admin'in altına tüm child hesapları ekle
      childUsersByParent[parentUser.username] = municipalityChildren.map(child => ({
        ...child,
        type: 'child',
        parentRole: 'Municipality-Admin',
        parentUsername: parentUser.username
      }));
    });

    // Admin kullanıcılarını ekle
    admins.forEach(parentUser => {
      parentUsers.push({ ...parentUser, type: 'parent', parentRole: 'Admin' });
      // Her Admin'in altına Example hesaplarını ekle
      childUsersByParent[parentUser.username] = exampleUsers.map(child => ({
        ...child,
        type: 'child',
        parentRole: 'Admin',
        parentUsername: parentUser.username
      }));
    });

    return { parentUsers, childUsersByParent, standaloneUsers };
  };

  const toggleParentExpansion = (parentUsername) => {
    setExpandedParents(prev => ({
      ...prev,
      [parentUsername]: !prev[parentUsername]
    }));
  };

  // Başka bir kullanıcıya giriş yap
  const handleLoginAsUser = async (targetUser) => {
    try {
      if (!targetUser || !targetUser.username) {
        setError('Kullanıcı bilgisi bulunamadı');
        return;
      }

      // Admin rolü kontrolü - Admin hesabına giriş yapılamaz
      if (targetUser.role === 'Admin' || targetUser.role?.toLowerCase() === 'admin') {
        setError('Admin hesabına giriş yapılamaz');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      // Admin olarak kullanıcının token'ını al
      const result = await cognitoAdminService.loginAsUser(targetUser.username);
      
      if (result.accessToken && result.idToken) {
        // Token'ları Amplify'in storage formatına uygun şekilde kaydet
        // Amplify v6 token'ları localStorage'da şu formatta saklar:
        // CognitoIdentityServiceProvider.{clientId}.{username}.{tokenType}
        const clientId = cognitoConfig.userPoolWebClientId;
        const username = targetUser.username;
        
        // Access token
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`,
          result.accessToken
        );
        
        // ID token
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`,
          result.idToken
        );
        
        // Refresh token
        if (result.refreshToken) {
          localStorage.setItem(
            `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`,
            result.refreshToken
          );
        }
        
        // LastAuthUser - hangi kullanıcının giriş yaptığını belirtir
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`,
          username
        );
        
        // Sayfayı yenile - AuthContext otomatik olarak yeni token'ları okuyacak
        window.location.reload();
      } else {
        throw new Error('Token alınamadı');
      }
    } catch (err) {
      console.error('Kullanıcıya giriş yapma hatası:', err);
      setError('Giriş yapılamadı: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.userManagement}>
      <div className={styles.userManagementHeader}>
        <div>
          <h2>Kullanıcı Yönetimi</h2>
          <p className={styles.userManagementSubtitle}>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button 
            className={styles.refreshButton}
            onClick={loadUsers}
            disabled={loading}
          >
            Yenile
          </button>
          <button 
            className={styles.addButton}
            onClick={() => setShowAddUser(true)}
          >
            + Yeni Kullanıcı Ekle
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: '12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            ✕
          </button>
        </div>
      )}

      {showAddUser && (
        <div className={styles.addUserModal}>
          <div className={styles.modalContent}>
            <h3>Yeni Kullanıcı Ekle</h3>
            <form onSubmit={handleAddUser}>
              <div className={styles.formGroup}>
                <label>E-posta</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Kullanıcı Adı</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Şifre</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={8}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Rol</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div className={styles.modalActions}>
                <button type="submit" disabled={isSubmitting} className={styles.submitButton}>
                  {isSubmitting ? 'Oluşturuluyor...' : 'Oluştur'}
                </button>
                <button type="button" onClick={() => setShowAddUser(false)} className={styles.cancelButton}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Kullanıcılar yükleniyor...</p>
        </div>
      ) : users.length === 0 ? (
        <div className={styles.emptyState}>
          <p>Henüz kullanıcı bulunmuyor.</p>
          <button 
            className={styles.addButton}
            onClick={() => setShowAddUser(true)}
          >
            İlk Kullanıcıyı Oluştur
          </button>
        </div>
      ) : (
        <div className={styles.usersTable}>
          <table>
            <thead>
              <tr>
                <th style={{ width: '30px' }}></th>
                <th>E-posta</th>
                <th>Rol</th>
                <th>Bölgeler</th>
                <th>Durum</th>
                <th>Oluşturulma</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const { parentUsers, childUsersByParent, standaloneUsers } = organizeUsersHierarchically(users);
                const result = [];

                // Parent kullanıcıları ve alt hesaplarını ekle
                parentUsers.forEach((parentUser) => {
                  const children = childUsersByParent[parentUser.username] || [];
                  const hasChildren = children.length > 0;
                  const isExpanded = expandedParents[parentUser.username];

                  // Parent kullanıcı
                  result.push(
                    <tr key={parentUser.username} style={{ backgroundColor: '#fafbfc' }}>
                      <td>
                        {hasChildren && (
                          <button
                            onClick={() => toggleParentExpansion(parentUser.username)}
                            style={{
                              background: 'none',
                              border: '1px solid #e2e8f0',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              padding: '2px 6px',
                              color: '#64748b',
                              fontWeight: '500',
                              lineHeight: '1',
                              transition: 'all 0.15s'
                            }}
                            title={isExpanded ? 'Alt hesapları gizle' : 'Alt hesapları göster'}
                          >
                            {isExpanded ? '\u25BE' : '\u25B8'}
                          </button>
                        )}
                      </td>
                      <td>
                        <strong>{parentUser.email}</strong>
                      </td>
                      <td>
                        <span className={styles.roleBadge}>{parentUser.role}</span>
                      </td>
                      <td>
                        {parentUser.regions && parentUser.regions.length > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span className={styles.regionsBadge} title={parentUser.regions.join(', ')}>
                              {parentUser.regions.length} bölge
                            </span>
                            <button
                              className={styles.regionsViewButton}
                              onClick={() => setViewingRegionsFor(viewingRegionsFor === parentUser.username ? null : parentUser.username)}
                              title="Bölgeleri gör"
                            >
                              {viewingRegionsFor === parentUser.username ? 'Gizle' : 'Gör'}
                            </button>
                            {viewingRegionsFor === parentUser.username && (
                              <div style={{
                                width: '100%',
                                marginTop: '8px',
                                padding: '10px',
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                borderRadius: '8px',
                                border: '1px solid rgba(59, 130, 246, 0.2)',
                                fontSize: '12px',
                                color: '#1e293b',
                                lineHeight: '1.6'
                              }}>
                                <strong style={{ display: 'block', marginBottom: '6px', color: '#2563eb' }}>
                                  Atanan Bölgeler:
                                </strong>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {parentUser.regions.map((region, idx) => (
                                    <span
                                      key={idx}
                                      style={{
                                        display: 'inline-block',
                                        padding: '4px 10px',
                                        background: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                        color: '#2563eb',
                                        fontWeight: '500',
                                        fontSize: '11px'
                                      }}
                                    >
                                      {region}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={styles.regionsBadgeEmpty}>Bölge atanmamış</span>
                        )}
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${parentUser.enabled ? styles.statusActive : styles.statusInactive}`}>
                          {parentUser.status}
                        </span>
                      </td>
                      <td>
                        {parentUser.userCreateDate ? new Date(parentUser.userCreateDate).toLocaleDateString('tr-TR') : 'N/A'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {parentUser.role !== 'Admin' && parentUser.role?.toLowerCase() !== 'admin' && (
                            <button
                              className={styles.loginButton}
                              onClick={() => handleLoginAsUser(parentUser)}
                              title="Bu hesaba giriş yap"
                            >
                              Giriş Yap
                            </button>
                          )}
                          <button
                            className={styles.editButton}
                            onClick={() => handleOpenRegionModal(parentUser)}
                            title="Bölgeleri düzenle"
                          >
                            Bölge Ata
                          </button>
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDeleteUser(parentUser.username)}
                            disabled={parentUser.username === user?.username}
                            title={parentUser.username === user?.username ? 'Kendi hesabınızı silemezsiniz' : ''}
                          >
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  );

                  // Alt hesaplar (açıksa göster)
                  if (isExpanded && hasChildren) {
                    children.forEach((childUser) => {
                      result.push(
                        <tr key={childUser.username} style={{ backgroundColor: '#ffffff' }}>
                          <td></td>
                          <td style={{ paddingLeft: '40px', position: 'relative' }}>
                            <span style={{
                              position: 'absolute',
                              left: '20px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#94a3b8',
                              fontSize: '12px'
                            }}>
                              <span style={{ display:'inline-block', width:6, height:6, borderLeft:'1.5px solid #cbd5e1', borderBottom:'1.5px solid #cbd5e1', marginBottom:2 }}></span>
                            </span>
                            <span style={{ color: '#475569', fontSize:'13px' }}>{childUser.email}</span>
                          </td>
                          <td>
                            <span className={styles.roleBadge} style={{ opacity: 0.8 }}>{childUser.role}</span>
                          </td>
                          <td>
                            {childUser.regions && childUser.regions.length > 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                <span className={styles.regionsBadge} title={childUser.regions.join(', ')}>
                                  {childUser.regions.length} bölge
                                </span>
                                <button
                                  className={styles.regionsViewButton}
                                  onClick={() => setViewingRegionsFor(viewingRegionsFor === childUser.username ? null : childUser.username)}
                                  title="Bölgeleri gör"
                                >
                                  {viewingRegionsFor === childUser.username ? 'Gizle' : 'Gör'}
                                </button>
                                {viewingRegionsFor === childUser.username && (
                                  <div style={{
                                    width: '100%',
                                    marginTop: '8px',
                                    padding: '10px',
                                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(59, 130, 246, 0.2)',
                                    fontSize: '12px',
                                    color: '#1e293b',
                                    lineHeight: '1.6'
                                  }}>
                                    <strong style={{ display: 'block', marginBottom: '6px', color: '#2563eb' }}>
                                      Atanan Bölgeler:
                                    </strong>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                      {childUser.regions.map((region, idx) => (
                                        <span
                                          key={idx}
                                          style={{
                                            display: 'inline-block',
                                            padding: '4px 10px',
                                            background: 'white',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(59, 130, 246, 0.3)',
                                            color: '#2563eb',
                                            fontWeight: '500',
                                            fontSize: '11px'
                                          }}
                                        >
                                          {region}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className={styles.regionsBadgeEmpty}>Bölge atanmamış</span>
                            )}
                          </td>
                          <td>
                            <span className={`${styles.statusBadge} ${childUser.enabled ? styles.statusActive : styles.statusInactive}`}>
                              {childUser.status}
                            </span>
                          </td>
                          <td>
                            {childUser.userCreateDate ? new Date(childUser.userCreateDate).toLocaleDateString('tr-TR') : 'N/A'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {childUser.role !== 'Admin' && childUser.role?.toLowerCase() !== 'admin' && (
                                <button
                                  className={styles.loginButton}
                                  onClick={() => handleLoginAsUser(childUser)}
                                  title="Bu hesaba giriş yap"
                                >
                                  Giriş Yap
                                </button>
                              )}
                              <button
                                className={styles.editButton}
                                onClick={() => handleOpenRegionModal(childUser)}
                                title="Bölgeleri düzenle"
                              >
                                Bölge Ata
                              </button>
                              <button
                                className={styles.deleteButton}
                                onClick={() => handleDeleteUser(childUser.username)}
                                disabled={childUser.username === user?.username}
                                title={childUser.username === user?.username ? 'Kendi hesabınızı silemezsiniz' : ''}
                              >
                                Sil
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  }
                });

                // Standalone kullanıcıları ekle
                standaloneUsers.forEach((userItem) => {
                  result.push(
                    <tr key={userItem.username}>
                      <td></td>
                      <td>
                        <strong>{userItem.email}</strong>
                      </td>
                      <td>
                        <span className={styles.roleBadge}>{userItem.role}</span>
                      </td>
                      <td>
                        {userItem.regions && userItem.regions.length > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                            <span className={styles.regionsBadge} title={userItem.regions.join(', ')}>
                              {userItem.regions.length} bölge
                            </span>
                            <button
                              className={styles.regionsViewButton}
                              onClick={() => setViewingRegionsFor(viewingRegionsFor === userItem.username ? null : userItem.username)}
                              title="Bölgeleri gör"
                            >
                              {viewingRegionsFor === userItem.username ? 'Gizle' : 'Gör'}
                            </button>
                            {viewingRegionsFor === userItem.username && (
                              <div style={{
                                width: '100%',
                                marginTop: '8px',
                                padding: '10px',
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                                borderRadius: '8px',
                                border: '1px solid rgba(59, 130, 246, 0.2)',
                                fontSize: '12px',
                                color: '#1e293b',
                                lineHeight: '1.6'
                              }}>
                                <strong style={{ display: 'block', marginBottom: '6px', color: '#2563eb' }}>
                                  Atanan Bölgeler:
                                </strong>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {userItem.regions.map((region, idx) => (
                                    <span
                                      key={idx}
                                      style={{
                                        display: 'inline-block',
                                        padding: '4px 10px',
                                        background: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                        color: '#2563eb',
                                        fontWeight: '500',
                                        fontSize: '11px'
                                      }}
                                    >
                                      {region}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={styles.regionsBadgeEmpty}>Bölge atanmamış</span>
                        )}
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${userItem.enabled ? styles.statusActive : styles.statusInactive}`}>
                          {userItem.status}
                        </span>
                      </td>
                      <td>
                        {userItem.userCreateDate ? new Date(userItem.userCreateDate).toLocaleDateString('tr-TR') : 'N/A'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {userItem.role !== 'Admin' && userItem.role?.toLowerCase() !== 'admin' && (
                            <button
                              className={styles.loginButton}
                              onClick={() => handleLoginAsUser(userItem)}
                              title="Bu hesaba giriş yap"
                            >
                              Giriş Yap
                            </button>
                          )}
                          <button
                            className={styles.editButton}
                            onClick={() => handleOpenRegionModal(userItem)}
                            title="Bölgeleri düzenle"
                          >
                            Bölge Ata
                          </button>
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDeleteUser(userItem.username)}
                            disabled={userItem.username === user?.username}
                            title={userItem.username === user?.username ? 'Kendi hesabınızı silemezsiniz' : ''}
                          >
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                });

                return result;
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* Bölge Atama Modal */}
      {selectedUserForRegions && (
        <div className={styles.addUserModal}>
          <div className={styles.modalContent} style={{ maxWidth: '700px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid rgba(59, 130, 246, 0.1)'
            }}>
              <div>
                <h3 style={{ margin: 0, color: '#1e293b', fontSize: '22px', fontWeight: '700' }}>
                  Bölge Ata
                </h3>
                <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                  {selectedUserForRegions.username}
                </p>
              </div>
            </div>
            
            <p style={{ 
              marginBottom: '24px', 
              color: '#64748b', 
              fontSize: '14px',
              lineHeight: '1.6',
              padding: '12px',
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}>
              Bu kullanıcıya erişim izni verilecek bölgeleri seçin. Municipality-Admin ve alt rolleri için kullanılır.
            </p>
            
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '12px', 
                fontWeight: '600',
                color: '#1e293b',
                fontSize: '15px'
              }}>
                Bölgeler (Çoklu Seçim):
              </label>
              <div style={{ 
                maxHeight: '350px', 
                overflowY: 'auto', 
                border: '2px solid rgba(59, 130, 246, 0.2)', 
                borderRadius: '12px',
                padding: '12px',
                background: 'white',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
              }}>
                {availableRegions.map(region => (
                  <label 
                    key={region}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      padding: '12px',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      marginBottom: '6px',
                      backgroundColor: userRegions.includes(region) 
                        ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' 
                        : 'transparent',
                      background: userRegions.includes(region) 
                        ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' 
                        : 'transparent',
                      border: userRegions.includes(region) 
                        ? '2px solid rgba(59, 130, 246, 0.4)' 
                        : '2px solid transparent',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!userRegions.includes(region)) {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!userRegions.includes(region)) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={userRegions.includes(region)}
                      onChange={() => handleRegionToggle(region)}
                      style={{ 
                        marginRight: '12px',
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        accentColor: '#3b82f6'
                      }}
                    />
                    <span style={{ 
                      fontSize: '14px',
                      fontWeight: userRegions.includes(region) ? '600' : '500',
                      color: userRegions.includes(region) ? '#1e40af' : '#1e293b'
                    }}>
                      {region}
                    </span>
                  </label>
                ))}
              </div>
              {userRegions.length > 0 && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '12px',
                  background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                  borderRadius: '8px',
                  border: '1px solid rgba(16, 185, 129, 0.3)'
                }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#065f46', fontWeight: '600' }}>
                    {userRegions.length} bölge seçildi:
                  </p>
                  <div style={{ 
                    marginTop: '8px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px'
                  }}>
                    {userRegions.map((region, idx) => (
                      <span
                        key={idx}
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          background: 'white',
                          borderRadius: '6px',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                          color: '#059669',
                          fontWeight: '500',
                          fontSize: '12px'
                        }}
                      >
                        {region}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.modalActions} style={{ 
              paddingTop: '20px',
              borderTop: '2px solid rgba(59, 130, 246, 0.1)'
            }}>
              <button 
                type="button" 
                onClick={handleSaveRegions} 
                disabled={isSubmitting} 
                className={styles.submitButton}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button 
                type="button" 
                onClick={handleCloseRegionModal} 
                className={styles.cancelButton}
                disabled={isSubmitting}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;

